import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {ChangePhoneNumberStep, MessageContent, PopupContent} from './Constant/Constant';
import {showAppLoader, hideAppLoader} from 'app/Components/Shared/Loader/LoaderSlice';
import ProviderManager from '../../../provider/providerManager';
import {Onboarding} from '../../Page/SelfCarePage/SelfCareAsyncThunk';
import {refreshToken} from 'app/utilities/authentication/authenticationSlice';
export const submitAccountValidation = createAsyncThunk('changePhoneNumber/submitAccountValidation', async (data, thunkAPI) => {
  thunkAPI.dispatch(showAppLoader());
  try {
    const result = await ProviderManager.getProviderData('submitChangePhoneNumberValidation', [data.subscriberNo]);
    thunkAPI.dispatch(hideAppLoader());
    return result.Body;
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return error;
  }
});

export const getNumberTypePrice = createAsyncThunk('changePhoneNumber/getNumberTypePrice', async (data, thunkAPI) => {
  thunkAPI.dispatch(showAppLoader());
  const {newNumbersData} = thunkAPI.getState().changePhoneNumber;
  const codesArray =
    newNumbersData?.map((item) => ({
      code: item.code,
      actRsnCode: item.actRsnCode,
    })) || [];

  const req = {
    Ban: data.ban,
    NumberType: codesArray,
  };
  try {
    const result = await ProviderManager.getProviderData('getNumberTypePrice', req);
    thunkAPI.dispatch(hideAppLoader());
    return result.Body;
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return {error: true};
  }
});

export const getNumbersByType = createAsyncThunk('changePhoneNumber/getNumbersByType', async (data, thunkAPI) => {
  const startTime = Date.now();
  const MIN_LOADING_TIME = 2000;

  try {
    const result = await ProviderManager.getProviderData('getNumbersByType', [data]);

    const elapsedTime = Date.now() - startTime;

    if (elapsedTime < MIN_LOADING_TIME) {
      await new Promise((resolve, reject) => {
        const remainingTime = MIN_LOADING_TIME - elapsedTime;
        const timerId = setTimeout(resolve, remainingTime);

        if (thunkAPI.signal.aborted) {
          clearTimeout(timerId);
          reject(new Error('Aborted'));
        }

        thunkAPI.signal.addEventListener('abort', () => {
          clearTimeout(timerId);
          reject(new Error('Aborted'));
        });
      });
    }

    return result.Body;
  } catch (error) {
    if (error.message === 'Aborted') {
      return thunkAPI.rejectWithValue({cancelled: true});
    }
    return {error: true};
  }
});

export const isNumberAvailable = createAsyncThunk('changePhoneNumber/isNumberAvailable', async (data, thunkAPI) => {
  thunkAPI.dispatch(showAppLoader());
  try {
    const result = await ProviderManager.getProviderData('isNumberAvailable', [data]);
    thunkAPI.dispatch(hideAppLoader());
    if (result.Body?.isSuccess) {
      const {newNumbersData, selectedAccount, newSelectedNumber} = thunkAPI.getState().changePhoneNumber;
      const selectedType = newNumbersData.find((numberType) => numberType.selected);
      const data = {
        SelectedPhoneNumber: selectedAccount.productSubscriberNo,
        Ban: selectedAccount.ban,
        PhoneNumberLevel: selectedType.code,
        NewPhoneNumber: newSelectedNumber,
        ReasonCode: selectedType.actRsnCode,
      };
      thunkAPI.dispatch(submitNewNumber(data));
    }
    return result.Body;
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return {error: true};
  }
});

export const submitNewNumber = createAsyncThunk('changePhoneNumber/submitNewNumber', async (data, thunkAPI) => {
  thunkAPI.dispatch(showAppLoader());
  try {
    const result = await ProviderManager.getProviderData('submitChangePhoneNumber', data);
    thunkAPI.dispatch(hideAppLoader());
    return result.Body;
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return {error: true};
  }
});

export const refreshTokenAfterChangePhoneNumber = createAsyncThunk(
  'changePhoneNumber/refreshTokenAfterChangePhoneNumber',
  async (data, thunkAPI) => {
    try {
      await thunkAPI.dispatch(refreshToken());
      return;
    } catch (error) {
      return;
    }
  },
);

function getErrorMessage(results) {
  let failureReason = results[0];
  switch (failureReason) {
    case 'InvalidParameters':
    case 'AccountNotFound':
      return MessageContent.GeneralError;
    case 'InvalidAccountType':
      return MessageContent.BusinessAccount;
    case 'Kosher':
      return MessageContent.KosherNumberError;
    case 'JetError':
      return MessageContent.GeneralError;
    case 'InvalidPaymentMethod':
    case 'SubscriberHasDebt':
      if (results.length > 1) {
        return MessageContent.DebtAndPaymentUpdateError;
      } else {
        if (failureReason === 'InvalidPaymentMethod') {
          return MessageContent.PaymentUpdateError;
        } else {
          return MessageContent.DebtError;
        }
      }

    case 'HasOpenFU':
      return MessageContent.OpenOrderError;
    case 'InvalidNPStatus':
      return MessageContent.NiudError;
    case 'SubscriberStatusIsSuspended':
      return MessageContent.SuspendedError;
    case 'SuspendedLostOrStolen':
      return MessageContent.SuspendedLostOrStolenError;
  }
}

// Helper function to compute typesData from newNumbersData
const computeTypesData = (newNumbersData) => {
  return newNumbersData.map((numberType, index) => ({
    id: numberType.code,
    label: `<div><div class='title'>${numberType.title}</div><div class='example'>${numberType.example}</div></div>`,
    rightContent: `<span class ="fw-600 letter-spacing">₪${numberType.price}</span> </br> <span>עלות חד פעמית</span>`,
    value: numberType.code,
    selected: numberType.selected,
    showRadioButtons: true,
  }));
};

const initialState = {
  currentStep: '',
  maxRequestCount: 0,
  showPopup: false,
  currentPopupData: null,
  generalMessage: null,
  selectAccountOptions: [],
  selectedAccount: null,
  newSelectedNumber: '',
  getNumberPricesCompleted: false,
  newNumbersDataLoading: false,
  newNumbersDataError: false,
  newNumbersData: [],
  typesData: [],
  debtPaymentUrl: '',
  updatePaymentUrl: '',
  saleUrl: '',
  contactUs: null,
  callBackCode: '',
  termLink: null,
};

const changePhoneNumberSlice = createSlice({
  name: 'changePhoneNumber',
  initialState: initialState,
  reducers: {
    resetState: () => initialState,
    setEpiData: (state, action) => {
      state.newNumbersData = action.payload.numberTypeConfiguration.value?.map((item) => ({
        title: item.title,
        example: item.example,
        code: item.code,
        actRsnCode: item.actRsnCode,
        numbers: [],
        selected: false,
        price: '',
        requestCounter: 0,
      }));

      state.typesData = computeTypesData(state.newNumbersData);
      state.debtPaymentUrl = action.payload.debtPayment.value;
      state.updatePaymentUrl = action.payload.updatePaymentMethod.value;
      state.saleUrl = action.payload.sale.value;
      state.termsLink = action.payload.termsLink.value;
      return state;
    },

    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
      return state;
    },
    setShowPopUp: (state, action) => {
      state.currentPopupData = action.payload.currentPopupData;
      state.showPopup = action.payload.showPopup;
      return state;
    },

    setGeneralMessage: (state, action) => {
      state.generalMessage = action.payload;
      state.currentStep = ChangePhoneNumberStep.GeneralMessage;
    },
    setSelectAccountOptions: (state, action) => {
      state.selectAccountOptions = action.payload;
      return state;
    },
    setSelectedAccount: (state, action) => {
      state.selectedAccount = action.payload;
      return state;
    },
    setSelectedNumberType: (state, action) => {
      // action.payload is the code of the number type to toggle selection
      state.newNumbersData.forEach((numberType) => {
        if (numberType.code === action.payload) {
          numberType.selected = !numberType.selected;
        } else {
          numberType.selected = false;
        }
      });
      state.typesData = computeTypesData(state.newNumbersData);
      state.newNumbersDataError = false;
      return state;
    },
    setSelectedNumber: (state, action) => {
      state.newSelectedNumber = action.payload;
      return state;
    },
    setPhoneCodeForCallBack: (state, action) => {
      state.callBackCode = action.payload;
      return state;
    },
    saveToHistory: (state, action) => {
      return state;
    },
  },
  extraReducers: {
    [submitAccountValidation.fulfilled]: (state, action) => {
      if (!action.payload?.isAxiosError && !action.payload.errorCode) {
        if (action.payload.isValid) {
          state.currentStep = ChangePhoneNumberStep.SelectNewNumber;
          return state;
        } else {
          if (action.payload.results?.length > 0) {
            state.generalMessage = getErrorMessage(action.payload.results);
          } else {
            state.generalMessage = MessageContent.GeneralError;
          }

          state.currentStep = ChangePhoneNumberStep.GeneralMessage;
          return state;
        }
      } else {
        state.generalMessage = MessageContent.GeneralError;
        state.currentStep = ChangePhoneNumberStep.GeneralMessage;
        return state;
      }
    },

    [getNumberTypePrice.fulfilled]: (state, action) => {
      if (!action.payload?.isAxiosError && !action.payload.errorCode && !action.payload.error && action.payload?.isSuccess) {
        action.payload?.prices.forEach((priceObj) => {
          const [code, price] = Object.entries(priceObj)[0];
          const numberType = state.newNumbersData.find((nt) => nt.code === code);
          if (numberType) {
            numberType.price = price;
          }
        });
        state.typesData = computeTypesData(state.newNumbersData);
        state.maxRequestCount = parseInt(action.payload?.maxRequestCount);
        state.getNumberPricesCompleted = true;
      } else {
        state.generalMessage = MessageContent.GeneralError;
        state.currentStep = ChangePhoneNumberStep.GeneralMessage;
      }
      return state;
    },
    [getNumbersByType.pending]: (state, action) => {
      state.newSelectedNumber = '';
      state.newNumbersDataError = false;
      state.newNumbersDataLoading = true;
      return state;
    },
    [getNumbersByType.fulfilled]: (state, action) => {
      state.newNumbersDataLoading = false;
      if (!action.payload?.isAxiosError && !action.payload.errorCode && !action.payload.error && action.payload?.length > 0) {
        const selectedTypeObj = state.newNumbersData.find((numberType) => numberType.selected);
        if (selectedTypeObj) {
          //selectedTypeObj.numbers.push(...action.payload);
          selectedTypeObj.numbers = action.payload;
          selectedTypeObj.requestCounter += 1;
        }
      } else {
        state.newNumbersDataError = true;
      }
      return state;
    },
    [getNumbersByType.rejected]: (state, action) => {
      state.newNumbersDataLoading = false;
      if (!action.payload?.cancelled) {
        state.newNumbersDataError = true;
      }
      return state;
    },
    [isNumberAvailable.fulfilled]: (state, action) => {
      if (!action.payload?.isAxiosError && !action.payload.errorCode && !action.payload.error) {
        if (!action.payload.isSuccess) {
          state.showPopup = true;
          state.currentPopupData = PopupContent.NotAvailablePopup;
        }
      } else {
        state.generalMessage = MessageContent.GeneralError;
        state.currentStep = ChangePhoneNumberStep.GeneralMessage;
      }
      return state;
    },
    [submitNewNumber.fulfilled]: (state, action) => {
      if (!action.payload?.isAxiosError && !action.payload.errorCode && !action.payload.error) {
        if (action.payload.isSuccess) {
          state.currentStep = ChangePhoneNumberStep.Success;
        } else {
          state.showPopup = true;
          state.currentPopupData = PopupContent.ErrorPopup;
        }
      } else {
        state.showPopup = true;
        state.currentPopupData = PopupContent.ErrorPopup;
      }
      return state;
    },
  },
});

export const {
  setEpiData,
  setGeneralMessage,
  setCurrentStep,
  setShowPopUp,
  resetState,
  setSelectedAccount,
  setSelectedNumberType,
  setSelectedNumber,
  saveToHistory,
  setSelectAccountOptions,
  setPhoneCodeForCallBack,
} = changePhoneNumberSlice.actions;

export default changePhoneNumberSlice.reducer;
